// SecretsPage.tsx — vault secret CRUD + ACL surface gated by authz.secrets.use.
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { BsArrowClockwise, BsClipboard, BsPlusLg, BsSave, BsTrash } from 'react-icons/bs'
import Text from 'common/ui/Text'
import Dropdown from 'common/ui/Dropdown'
import Expando, { ExpandoChevron } from 'common/ui/Expando'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { apiFetch, useAuth } from '../hooks/useAuth'

interface Secret {
    name: string
    url: string
    value?: string
    description?: string
    created_by?: string
    created_at?: string
    updated_at?: string
}

interface ACLEntry {
    secret_name: string
    user_email: string
    permission: 'read' | 'write'
    granted_by?: string
    granted_at?: string
}

// Tree node for the filesystem-style viewer. `label` is the displayed segment
// (possibly merged across single-child chains); `secret` is set iff the node
// itself names a secret. A node may have BOTH a secret and children.
interface TreeNode {
    label: string
    fullPath: string
    secret?: Secret
    children: TreeNode[]
}

// Build a path-compressed trie of secrets. Any node with no own-secret and
// exactly one child is merged with its child — recursively — so a chain of
// single-child folders renders as one row regardless of depth.
const buildSecretTree = (secrets: Secret[]): TreeNode[] => {
    interface Raw {
        segment: string
        children: Map<string, Raw>
        secret?: Secret
    }
    const root: Raw = { segment: '', children: new Map() }
    for (const s of secrets) {
        let node = root
        for (const part of s.name.split('/').filter(Boolean)) {
            if (!node.children.has(part)) node.children.set(part, { segment: part, children: new Map() })
            node = node.children.get(part)!
        }
        node.secret = s
    }
    const compress = (raw: Raw, parentPath: string): TreeNode => {
        const labels = [raw.segment]
        let cur = raw
        while (!cur.secret && cur.children.size === 1) {
            const next = cur.children.values().next().value as Raw
            labels.push(next.segment)
            cur = next
        }
        const label = labels.filter(Boolean).join('/')
        const fullPath = parentPath ? `${parentPath}/${label}` : label
        const children = Array.from(cur.children.values())
            .map((c) => compress(c, fullPath))
            .sort(treeCompare)
        return { label, fullPath, secret: cur.secret, children }
    }
    return Array.from(root.children.values())
        .map((c) => compress(c, ''))
        .sort(treeCompare)
}

const treeCompare = (a: TreeNode, b: TreeNode): number => {
    const aLeaf = a.children.length === 0
    const bLeaf = b.children.length === 0
    if (aLeaf !== bLeaf) return aLeaf ? 1 : -1
    return a.label.localeCompare(b.label)
}

const countDescendantSecrets = (node: TreeNode): number => {
    let n = node.secret ? 1 : 0
    for (const c of node.children) n += countDescendantSecrets(c)
    return n
}

interface FolderRowProps {
    node: TreeNode
    depth: number
    renderChildren: (depth: number) => ReactNode
}

const FolderRow = ({ node, depth, renderChildren }: FolderRowProps) => {
    const count = countDescendantSecrets(node)
    return (
        <Expando>
            {({ open, toggle }) => (
                <Fragment>
                    <tr>
                        <td className="align-middle">
                            <div style={{ paddingLeft: `${depth * 1.5}em` }} className="d-flex align-items-center gap-2">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 text-muted"
                                    onClick={toggle}
                                    aria-expanded={open}
                                    aria-label={open ? 'Collapse folder' : 'Expand folder'}
                                >
                                    <ExpandoChevron open={open} />
                                </Button>
                                <code className="text-muted">{node.label}/</code>
                            </div>
                        </td>
                        <td className="text-muted">{count} secret{count === 1 ? '' : 's'}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                    {open && renderChildren(depth + 1)}
                </Fragment>
            )}
        </Expando>
    )
}

const SecretsPage = () => {
    const { email, scopes, loading, error: authError } = useAuth()
    const isAdmin = scopes.includes('authz.admin')

    const [secrets, setSecrets] = useState<Secret[]>([])
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    const [newPath, setNewPath] = useState('')
    const [newValue, setNewValue] = useState('')
    const [newDescription, setNewDescription] = useState('')

    const [openPath, setOpenPath] = useState<string | null>(null)
    const [openSecret, setOpenSecret] = useState<Secret | null>(null)
    const [openACL, setOpenACL] = useState<ACLEntry[]>([])
    const [openErr, setOpenErr] = useState('')
    const [editValue, setEditValue] = useState('')
    const [editDescription, setEditDescription] = useState('')

    const [aclEmail, setAclEmail] = useState('')
    const [aclPerm, setAclPerm] = useState<'read' | 'write'>('read')

    const flash = (msg: string, isError = false) => {
        if (isError) { setError(msg); setSuccess('') } else { setSuccess(msg); setError('') }
        setTimeout(() => { setError(''); setSuccess('') }, 4000)
    }

    const encodePath = (p: string) => p.split('/').map(encodeURIComponent).join('/')

    const fetchSecrets = useCallback(async () => {
        try {
            const res = await apiFetch('/api/secrets')
            if (res.status === 403) {
                setError('You need the authz.secrets.use scope to view secrets.')
                return
            }
            if (!res.ok) {
                const d = await res.json().catch(() => ({}))
                setError(d.error || 'Failed to load secrets')
                return
            }
            const data = await res.json()
            setSecrets(data.secrets || [])
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Network error')
        }
    }, [])

    useEffect(() => {
        if (!loading && email) fetchSecrets()
    }, [loading, email, fetchSecrets])

    const tree = useMemo(() => buildSecretTree(secrets), [secrets])

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault()
        const path = newPath.trim().replace(/^\/+|\/+$/g, '')
        if (!path || !newValue) { flash('Path and value are required', true); return }
        const res = await apiFetch(`/api/secrets/${encodePath(path)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: newValue, description: newDescription }),
        })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Failed (${res.status})`, true)
            return
        }
        flash(`Created ${path}`)
        setNewPath(''); setNewValue(''); setNewDescription('')
        fetchSecrets()
    }

    const openSecretRow = async (path: string) => {
        if (openPath === path) { setOpenPath(null); setOpenSecret(null); setOpenACL([]); return }
        setOpenPath(path); setOpenSecret(null); setOpenACL([]); setOpenErr('')
        try {
            const [sRes, aRes] = await Promise.all([
                apiFetch(`/api/secrets/${encodePath(path)}`),
                apiFetch(`/api/secrets/${encodePath(path)}/acl`),
            ])
            if (sRes.ok) {
                const s = await sRes.json()
                setOpenSecret(s)
                setEditValue(s.value || '')
                setEditDescription(s.description || '')
            } else {
                const d = await sRes.json().catch(() => ({}))
                setOpenErr(d.error || `Failed to load (${sRes.status})`)
            }
            if (aRes.ok) {
                const d = await aRes.json()
                setOpenACL(d.acl || [])
            }
        } catch (e: unknown) {
            setOpenErr(e instanceof Error ? e.message : 'Network error')
        }
    }

    const handleSaveEdit = async () => {
        if (!openPath) return
        const res = await apiFetch(`/api/secrets/${encodePath(openPath)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: editValue, description: editDescription }),
        })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Save failed (${res.status})`, true)
            return
        }
        flash(`Updated ${openPath}`)
        fetchSecrets()
    }

    const [confirm, confirmDialog] = useConfirm()

    const handleDelete = async (path: string) => {
        const ok = await confirm({
            title: 'Delete Secret',
            body: <>Delete secret <code>{path}</code>? This is irreversible.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        const res = await apiFetch(`/api/secrets/${encodePath(path)}`, { method: 'DELETE' })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Delete failed (${res.status})`, true)
            return
        }
        flash(`Deleted ${path}`)
        if (openPath === path) { setOpenPath(null); setOpenSecret(null); setOpenACL([]) }
        fetchSecrets()
    }

    const handleGrant = async () => {
        if (!openPath || !aclEmail) return
        const res = await apiFetch(`/api/secrets/${encodePath(openPath)}/acl/${encodeURIComponent(aclEmail)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ permission: aclPerm }),
        })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Grant failed (${res.status})`, true)
            return
        }
        flash(`Granted ${aclPerm} to ${aclEmail}`)
        setAclEmail('')
        const aRes = await apiFetch(`/api/secrets/${encodePath(openPath)}/acl`)
        if (aRes.ok) {
            const d = await aRes.json()
            setOpenACL(d.acl || [])
        }
    }

    const handleRevoke = async (user: string) => {
        if (!openPath) return
        const ok = await confirm({
            title: 'Revoke Access',
            body: <>Revoke <strong>{user}</strong>'s access to <code>{openPath}</code>?</>,
            confirmLabel: 'Revoke',
        })
        if (!ok) return
        const res = await apiFetch(`/api/secrets/${encodePath(openPath)}/acl/${encodeURIComponent(user)}`, { method: 'DELETE' })
        if (!res.ok) {
            const d = await res.json().catch(() => ({}))
            flash(d.error || `Revoke failed (${res.status})`, true)
            return
        }
        flash(`Revoked ${user}`)
        const aRes = await apiFetch(`/api/secrets/${encodePath(openPath)}/acl`)
        if (aRes.ok) {
            const d = await aRes.json()
            setOpenACL(d.acl || [])
        }
    }

    const renderNodes = (nodes: TreeNode[], depth: number): ReactNode =>
        nodes.map((node) =>
            node.children.length === 0 && node.secret
                ? renderLeaf(node, depth)
                : (
                    <FolderRow
                        key={node.fullPath}
                        node={node}
                        depth={depth}
                        renderChildren={(d) => renderNodes(node.children, d)}
                    />
                ),
        )

    const renderLeaf = (node: TreeNode, depth: number): ReactNode => {
        const s = node.secret!
        const isOpen = openPath === s.name
        return (
            <Fragment key={node.fullPath}>
                <tr>
                    <td className="align-middle">
                        <div style={{ paddingLeft: `${depth * 1.5}em` }} className="d-flex align-items-center gap-2">
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={() => openSecretRow(s.name)}
                                aria-expanded={isOpen}
                                aria-label={isOpen ? 'Collapse secret' : 'Expand secret'}
                            >
                                <ExpandoChevron open={isOpen} />
                            </Button>
                            <code>{node.label}</code>
                        </div>
                    </td>
                    <td className="text-muted">{s.description || '—'}</td>
                    <td className="text-muted">{s.created_by || '—'}</td>
                    <td className="text-muted">{s.updated_at?.slice(0, 19).replace('T', ' ') || '—'}</td>
                    <td className="text-end">
                        {isAdmin && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(s.name)}
                                className="d-inline-flex align-items-center gap-1"
                            >
                                <BsTrash /> Delete
                            </Button>
                        )}
                    </td>
                </tr>
                {isOpen && (
                    <tr>
                        <td colSpan={5}>
                            {openErr && <Alert variant="danger">{openErr}</Alert>}
                            {!openSecret && !openErr && <span className="text-muted small">Loading…</span>}
                            {openSecret && (
                                <Card className="mb-3">
                                    <Card.Header>
                                        <code>{openSecret.url}</code>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form.Group className="mb-3" controlId={`secret-value-${s.name}`}>
                                            <Form.Label>Value</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3" controlId={`secret-description-${s.name}`}>
                                            <Form.Label>Description</Form.Label>
                                            <Text
                                                type="text"
                                                style={{ width: "100%" }}
                                                value={editDescription}
                                                onChange={(e) => setEditDescription(e.target.value)}
                                            />
                                        </Form.Group>

                                        <hr className="my-3" />

                                        <h6 className="mb-2">Access Control List</h6>
                                        {openACL.length === 0 ? (
                                            <span className="text-muted small">No grants yet.</span>
                                        ) : (
                                            <Table size="sm" borderless className="m-0">
                                                <tbody>
                                                    {openACL.map((a) => (
                                                        <tr key={a.user_email}>
                                                            <td><code>{a.user_email}</code></td>
                                                            <td>
                                                                <Badge bg={a.permission === 'write' ? 'warning' : 'secondary'}>
                                                                    {a.permission}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-muted">
                                                                {a.granted_by ? `by ${a.granted_by}` : ''}
                                                            </td>
                                                            <td className="text-end">
                                                                {isAdmin && (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleRevoke(a.user_email)}
                                                                        className="d-flex align-items-center gap-1 ms-auto"
                                                                    >
                                                                        <BsTrash /> Revoke
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}

                                        {isAdmin && (
                                            <Form.Group className="mt-3">
                                                <Form.Label>Grant to</Form.Label>
                                                <div className="d-flex flex-wrap align-items-center gap-2">
                                                    <Text
                                                        type="email"
                                                        placeholder="user@example.com"
                                                        value={aclEmail}
                                                        onChange={(e) => setAclEmail(e.target.value)}
                                                        style={{ width: "100%", maxWidth: '20rem' }}
                                                    />
                                                    <Dropdown
                                                        value={aclPerm}
                                                        onChange={(e) => setAclPerm(e.target.value as 'read' | 'write')}
                                                        options={[
                                                            { value: 'read', label: 'read' },
                                                            { value: 'write', label: 'write' },
                                                        ]}
                                                    />
                                                    <Button variant="success" onClick={handleGrant} className="d-flex align-items-center gap-2">
                                                        <BsPlusLg /> Grant
                                                    </Button>
                                                </div>
                                            </Form.Group>
                                        )}
                                    </Card.Body>
                                    <Card.Footer className="d-flex gap-2">
                                        <Button variant="primary" onClick={handleSaveEdit} className="d-flex align-items-center gap-2">
                                            <BsSave /> Save
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => { navigator.clipboard?.writeText(editValue); flash('Value copied to clipboard') }}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <BsClipboard /> Copy value
                                        </Button>
                                    </Card.Footer>
                                </Card>
                            )}
                        </td>
                    </tr>
                )}
            </Fragment>
        )
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <>
            <h1 className="mb-4">Secrets</h1>
            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {isAdmin && (
                <Card className="mb-3">
                    <Card.Header>Create Secret</Card.Header>
                    <Card.Body>
                        <Form id="secrets-create-form" onSubmit={handleCreate}>
                            <Form.Group className="mb-3">
                                <Form.Label>Path</Form.Label>
                                <Text
                                    type="text"
                                    style={{ width: "100%" }}
                                    placeholder="path/like/this"
                                    value={newPath}
                                    onChange={(e) => setNewPath(e.target.value)}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Text
                                    type="text"
                                    style={{ width: "100%" }}
                                    placeholder="optional"
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Value</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="value (plaintext or JSON)"
                                    value={newValue}
                                    onChange={(e) => setNewValue(e.target.value)}
                                    required
                                />
                            </Form.Group>
                        </Form>
                    </Card.Body>
                    <Card.Footer className="d-flex align-items-center justify-content-between gap-3">
                        <Button variant="success" type="submit" form="secrets-create-form" className="d-flex align-items-center gap-2">
                            <BsPlusLg /> Create
                        </Button>
                        {newPath && (
                            <span className="text-muted small">
                                URL will be{' '}
                                <code>authz://&lt;host&gt;/{newPath.replace(/^\/+|\/+$/g, '')}</code>
                            </span>
                        )}
                    </Card.Footer>
                </Card>
            )}

            <Card className="mb-3">
                <Card.Header className="d-flex align-items-center justify-content-between">
                    <span>Secrets visible to you</span>
                    <Button variant="link" className="p-0 d-flex align-items-center gap-1" onClick={fetchSecrets}>
                        <BsArrowClockwise /> Refresh
                    </Button>
                </Card.Header>
                <Card.Body>
                    {secrets.length === 0 ? (
                        <span className="text-muted small">
                            No secrets — {isAdmin ? 'create one above.' : 'ask an admin for access.'}
                        </span>
                    ) : (
                        <Table size="sm" hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th>Path</th>
                                    <th>Description</th>
                                    <th>Created by</th>
                                    <th>Updated</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {renderNodes(tree, 0)}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {confirmDialog}
        </>
    )
}

export default SecretsPage
